/**
 * Tenant terminology as plain data — deliberately zod-free.
 *
 * Same reason as `features.ts`: the web console resolves its term set before
 * the first render (`apps/web/src/lib/terminology.ts`), so importing these from
 * the zod settings schema put zod and the 46 KB schema module in the entry
 * chunk to read a table of nouns.
 *
 * `settings.schema.ts` re-exports everything here and keeps `TerminologySettingsSchema`
 * as the validation source of truth.
 */

// ---------------------------------------------------------------------------
// Terminology — tenants run one of two built-in word sets and may override any
// individual term. `employees`/`employee` label staff who sign in and self
// check-in, while `workers`/`worker` label field/site staff who punch on the
// shared tablet app. UI surfaces pick whichever noun matches who that screen
// is for.
// ---------------------------------------------------------------------------

export type TerminologyProfile = 'hr' | 'construction';

export type TerminologyTermKey =
  | 'employees'
  | 'employee'
  | 'workers'
  | 'worker'
  | 'worksites'
  | 'worksite'
  | 'team';

/** A resolved word set — every term filled in. */
export type TerminologyTerms = Record<TerminologyTermKey, string>;

export const TERMINOLOGY_DEFAULTS: Record<TerminologyProfile, TerminologyTerms> = {
  hr: {
    employees: 'Employees',
    employee: 'Employee',
    workers: 'Workers',
    worker: 'Worker',
    worksites: 'Worksites',
    worksite: 'Worksite',
    team: 'Team',
  },
  construction: {
    employees: 'Staff',
    employee: 'Staff member',
    workers: 'Workers',
    worker: 'Worker',
    worksites: 'Sites & Camps',
    worksite: 'Site',
    team: 'Crew',
  },
};

/**
 * The stored shape `resolveTerminology` reads. Declared structurally rather
 * than as `z.infer<typeof TerminologySettingsSchema>` so this module stays
 * zod-free; the schema in `settings.schema.ts` produces exactly this shape and
 * asserts as much at compile time.
 */
export interface TerminologySettingsInput {
  profile: TerminologyProfile;
  overrides?: Partial<Record<TerminologyTermKey, string | undefined>>;
}

/** Resolve the effective term set: profile defaults with non-empty overrides applied. */
export function resolveTerminology(setting: TerminologySettingsInput): TerminologyTerms {
  const base = TERMINOLOGY_DEFAULTS[setting.profile] ?? TERMINOLOGY_DEFAULTS.hr;
  const overrides = setting.overrides ?? {};
  const out = { ...base };
  (Object.keys(out) as TerminologyTermKey[]).forEach((k) => {
    const v = overrides[k];
    if (typeof v === 'string' && v.trim()) out[k] = v.trim();
  });
  return out;
}
